import { Box, Grid } from '@mui/material'
import {
    ReactElement,
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
} from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import {
    setSelectedBuilding,
    updateHierarchyLevelData,
    setSiteList,
    setSiteBuildingAssetCount,
} from '../../../../redux/slices/facilityMonitoringSlice'
import {
    fetchRecordRelationDataByID,
    fetchEntityRecordsList,
    getBuildingsAssetsCount,
} from '../../../../services/entityServices'
import AttributeTable from './BuildingAttributes'
import { RelatedEntityInstance } from '../../../../services/apiInterfaces'
import { AssetSensorTable } from './AssetSensorTable'
import {
    EntityInstanceTypes,
    relationStatus,
    UserPermissions,
    URLSearchParamsKeys,
    EntityStatus,
    RoutePaths,
} from '../../../../constants/constants'
import { getRelationNameByTargetEntityTemplateId } from '../../../../utils/RelationUtils'
import { useGetAssetsAndSensorsInBuilding } from '../../../../CustomHooks/FacilityMonitoring/getAssetsAndSensorsInBuilding'
import GenericDropdown from '../../Intransit/GenericDropdown'
import ErrorBoundary from '../../../ErrorBoundaries/ErrorBoundary'
import { useTranslation } from 'react-i18next'
import { SensorFloorMapping } from './SensorFloorMapping'
import { findEntityById } from '../../../../utils/EntityUtils'
import EntityInstance from '../../../Entity/EntityInstance'
import { useQueryClient } from '@tanstack/react-query'
import { IBuildingOption, IHierarchyLevelConfig } from '../interfaces'
import { prepareSiteList } from '../Utils/FMUtils'

export const SiteView = (): ReactElement => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const queryClient = useQueryClient()
    const [searchParams, setSearchParams] = useSearchParams()
    const location = useLocation()
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const navList = useAppSelector((state) => state.nav.navList)
    const user = useAppSelector((state) => state.user.user)
    const {
        hierarchyConfig,
        hierarchyData,
        buildingEntityTemplateId,
        topLevelBuildings,
        buildingNameAttributeId,
        buildingFloorLayoutAttributeId,
        selectedBuilding,
        siteEntityTemplateId,
        siteLocationAttributeId,
        siteNameAttributeId,
    } = fmSlice

    const orgSlice = useAppSelector((state) => state.org)
    const { org, permissions } = orgSlice
    const orgId = org?.id ? org.id : ''

    const hasWritePermission = permissions.includes(
        UserPermissions.ENTITY_WRITER,
    )

    const [dynamicSelections, setDynamicSelections] = useState<
        Record<number, string>
    >({})
    const initStatus = useRef<'pending' | 'started' | 'completed'>('pending')
    const [dynamicEntities, setDynamicEntities] = useState<
        Record<number, RelatedEntityInstance[]>
    >({})
    const [dynamicLoading, setDynamicLoading] = useState<
        Record<number, boolean>
    >({})

    const [buildingsAtSite, setBuildingsAtSite] = useState<IBuildingOption[]>(
        [],
    )
    const [selectedBuildingUID, setSelectedBuildingUID] = useState<string>('')
    const [loadingBuildings, setLoadingBuildings] = useState<boolean>(false)

    // Add/Edit entity instance state
    const [openEntityInstance, setOpenEntityInstance] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [entityData, setEntityData] = useState({
        templateName: '',
        templateId: '',
        entityId: '',
        orgId: '',
    })
    const [parentChildRelationName, setParentChildRelationName] =
        useState<string>('')
    // Track which level is being added/edited for reloads
    const [editingLevel, setEditingLevel] = useState<
        number | 'building' | null
    >(null)

    const buildingEntityTemplateName = useMemo(
        () => findEntityById(navList, buildingEntityTemplateId).name,
        [navList, buildingEntityTemplateId],
    )

    const buildingName = useMemo(
        () =>
            buildingEntityTemplateName
                ? t(buildingEntityTemplateName)
                : t('Building'),
        [buildingEntityTemplateName, t],
    )

    const getInitialSelectionForLevel = useCallback(
        (config: IHierarchyLevelConfig, index: number) => {
            if (config.entityTemplateId === siteEntityTemplateId) {
                return (
                    searchParams.get(URLSearchParamsKeys.selectedSiteUID) || ''
                )
            }
            return (
                searchParams.get(
                    `${URLSearchParamsKeys.dynamicLevelPrefix}${index}`,
                ) || ''
            )
        },
        [siteEntityTemplateId, searchParams],
    )

    const updateURLParams = useCallback(
        (
            newSelections: Record<number, string>,
            newBuildingUID: string,
            entitiesOverride?: Record<number, RelatedEntityInstance[]>,
        ) => {
            if (initStatus.current !== 'completed') return

            // Prevent hijack of React Router transition when navigating away via SideNav
            if (!location.pathname.includes(RoutePaths.facilityMonitoring))
                return

            const paramsMap: Record<string, string> = {}

            hierarchyConfig.forEach((config, idx) => {
                const selInstanceId = newSelections[idx]
                if (!selInstanceId) return

                const entities =
                    (entitiesOverride
                        ? entitiesOverride[idx]
                        : dynamicEntities[idx]) || []
                const matchedEntity = entities.find(
                    (e) => e.entityInstanceId === selInstanceId,
                )
                const val = matchedEntity?.uid || ''

                if (config.entityTemplateId === siteEntityTemplateId) {
                    paramsMap[URLSearchParamsKeys.selectedSiteUID] = val
                } else {
                    paramsMap[
                        `${URLSearchParamsKeys.dynamicLevelPrefix}${idx}`
                    ] = val
                }
            })

            if (newBuildingUID) {
                paramsMap[URLSearchParamsKeys.selectedBuildingUID] =
                    newBuildingUID
            }

            const keysToCheck = [
                ...hierarchyConfig.map((c, i) =>
                    c.entityTemplateId === siteEntityTemplateId
                        ? URLSearchParamsKeys.selectedSiteUID
                        : `${URLSearchParamsKeys.dynamicLevelPrefix}${i}`,
                ),
                URLSearchParamsKeys.selectedBuildingUID,
            ]

            setSearchParams(
                (prevParams) => {
                    const nextParams = new URLSearchParams(prevParams)
                    let changed = false
                    keysToCheck.forEach((key) => {
                        const currentVal = nextParams.get(key) || ''
                        const newVal = paramsMap[key] || ''
                        if (currentVal !== newVal) {
                            if (newVal) {
                                nextParams.set(key, newVal)
                            } else {
                                nextParams.delete(key)
                            }
                            changed = true
                        }
                    })
                    return changed ? nextParams : prevParams
                },
                { replace: true },
            )
        },
        [
            setSearchParams,
            hierarchyConfig,
            siteEntityTemplateId,
            dynamicEntities,
            location.pathname,
        ],
    )

    /** Fetch children for a given parent level and auto-select the first child */
    const fetchChildrenForLevel = useCallback(
        async (
            levelIndex: number,
            entityInstanceId: string,
            isInitialLoad = false,
            currentSelections?: Record<number, string>,
            currentBuildingUID?: string,
            currentEntitiesMap?: Record<number, RelatedEntityInstance[]>,
        ) => {
            if (levelIndex === hierarchyConfig.length - 1) {
                // Last pre-building level → fetch buildings
                setLoadingBuildings(true)
                let targetBuilding: IBuildingOption | null = null
                try {
                    const parentTemplateId =
                        hierarchyConfig[levelIndex].entityTemplateId
                    const response = await fetchRecordRelationDataByID(
                        orgId,
                        parentTemplateId,
                        entityInstanceId,
                    )

                    const relationName =
                        getRelationNameByTargetEntityTemplateId(
                            hierarchyData[levelIndex].relations,
                            buildingEntityTemplateId,
                        )

                    const activeBuildings =
                        response?.relations?.[relationName]?.filter(
                            (inst: RelatedEntityInstance) =>
                                inst.status === relationStatus.ACTIVE,
                        ) || []

                    // Map to lean IBuildingOption — no attribute shape mismatch
                    const buildingOptions: IBuildingOption[] =
                        activeBuildings.map((b) => ({
                            id:
                                b.attributes?.find((a) => a.id === 'Id')
                                    ?.value || '',
                            entityInstanceId: b.entityInstanceId,
                            uid: b.uid,
                            name: b.uid,
                            canEdit: hasWritePermission,
                            layout:
                                b.attributes?.find(
                                    (a) =>
                                        a.id === buildingFloorLayoutAttributeId,
                                )?.value || '',
                            buildingName:
                                b.attributes?.find(
                                    (a) => a.id === buildingNameAttributeId,
                                )?.value || b.uid,
                            licenseDuration:
                                b.attributes?.find(
                                    (a) => a.id === 'licenseDuration',
                                )?.value || '',
                        }))
                    setBuildingsAtSite(buildingOptions)

                    // Auto-select building: from URL if initial load, fallback to current selection or first building
                    const currentSelection =
                        currentBuildingUID || selectedBuildingUID
                    const urlBuildingUID = isInitialLoad
                        ? searchParams.get(
                              URLSearchParamsKeys.selectedBuildingUID,
                          )
                        : null
                    const targetBuildingSelection =
                        urlBuildingUID || currentSelection
                    const matchedBuilding = targetBuildingSelection
                        ? buildingOptions.find(
                              (b) =>
                                  b.uid === targetBuildingSelection ||
                                  b.entityInstanceId ===
                                      targetBuildingSelection,
                          )
                        : null
                    targetBuilding = matchedBuilding || buildingOptions[0]
                    if (targetBuilding) {
                        setSelectedBuildingUID(targetBuilding.uid)
                        dispatch(setSelectedBuilding(targetBuilding))
                    }
                } catch (_) {
                    // silently ignore fetch failures
                } finally {
                    setLoadingBuildings(false)
                    if (isInitialLoad) {
                        initStatus.current = 'completed'
                    }
                    // Sync the fully loaded selection state to the URL
                    updateURLParams(
                        currentSelections || dynamicSelections,
                        targetBuilding?.uid || currentBuildingUID || '',
                        currentEntitiesMap,
                    )
                }
            } else {
                // Fetch next dynamic level
                const nextLevelIndex = levelIndex + 1
                setDynamicLoading((prev) => ({
                    ...prev,
                    [nextLevelIndex]: true,
                }))
                try {
                    const parentTemplateId =
                        hierarchyConfig[levelIndex].entityTemplateId
                    const targetTemplateId =
                        hierarchyConfig[nextLevelIndex].entityTemplateId
                    const response = await fetchRecordRelationDataByID(
                        orgId,
                        parentTemplateId,
                        entityInstanceId,
                    )

                    const relationName =
                        getRelationNameByTargetEntityTemplateId(
                            hierarchyData[levelIndex].relations,
                            targetTemplateId,
                        )

                    const activeEntities =
                        response?.relations?.[relationName]?.filter(
                            (inst: RelatedEntityInstance) =>
                                inst.status === relationStatus.ACTIVE,
                        ) || []

                    setDynamicEntities((prev) => ({
                        ...prev,
                        [nextLevelIndex]: activeEntities,
                    }))

                    const nextEntitiesMap = {
                        ...(currentEntitiesMap || dynamicEntities),
                        [nextLevelIndex]: activeEntities,
                    }

                    // Auto-select next level: from URL if initial load, fallback to current selection or first child entity
                    const sel = currentSelections || dynamicSelections
                    const currentSelection = sel[nextLevelIndex]
                    const urlSelection = isInitialLoad
                        ? getInitialSelectionForLevel(
                              hierarchyConfig[nextLevelIndex],
                              nextLevelIndex,
                          )
                        : null
                    const targetSelection = urlSelection || currentSelection
                    const matchedChild = targetSelection
                        ? activeEntities.find(
                              (inst: RelatedEntityInstance) =>
                                  inst.uid === targetSelection ||
                                  inst.entityInstanceId === targetSelection,
                          )
                        : null
                    const targetChild = matchedChild || activeEntities[0]
                    if (targetChild) {
                        const updatedSelections = {
                            ...sel,
                            [nextLevelIndex]: targetChild.entityInstanceId,
                        }
                        setDynamicSelections(updatedSelections)
                        // Recurse to auto-select all the way down
                        await fetchChildrenForLevel(
                            nextLevelIndex,
                            targetChild.entityInstanceId,
                            isInitialLoad,
                            updatedSelections,
                            currentBuildingUID,
                            nextEntitiesMap,
                        )
                    } else {
                        if (isInitialLoad) {
                            initStatus.current = 'completed'
                        }
                        updateURLParams(
                            sel,
                            currentBuildingUID || '',
                            nextEntitiesMap,
                        )
                    }
                } catch (_) {
                    // silently ignore fetch failures
                } finally {
                    setDynamicLoading((prev) => ({
                        ...prev,
                        [nextLevelIndex]: false,
                    }))
                }
            }
        },
        [
            hierarchyConfig,
            orgId,
            hierarchyData,
            buildingEntityTemplateId,
            selectedBuildingUID,
            searchParams,
            hasWritePermission,
            buildingFloorLayoutAttributeId,
            buildingNameAttributeId,
            dispatch,
            updateURLParams,
            dynamicSelections,
            dynamicEntities,
            getInitialSelectionForLevel,
        ],
    )

    const handleDynamicChange = useCallback(
        async (levelIndex: number, value: string) => {
            const entityInstanceId = value
            const next = {
                ...dynamicSelections,
                [levelIndex]: entityInstanceId,
            }
            // Clear all subsequent selections
            for (let i = levelIndex + 1; i < hierarchyConfig.length; i++) {
                next[i] = ''
            }
            setDynamicSelections(next)
            setSelectedBuildingUID('')
            dispatch(setSelectedBuilding({}))

            // Proactively clear subsequent entity lists and buildings so stale data doesn't linger
            setDynamicEntities((prev) => {
                const nextEntities = { ...prev }
                for (let i = levelIndex + 1; i < hierarchyConfig.length; i++) {
                    nextEntities[i] = []
                }
                return nextEntities
            })
            setBuildingsAtSite([])

            if (!entityInstanceId) {
                updateURLParams(next, '')
                return
            }

            await fetchChildrenForLevel(
                levelIndex,
                entityInstanceId,
                false,
                next,
            )
        },
        [
            hierarchyConfig,
            fetchChildrenForLevel,
            dispatch,
            dynamicSelections,
            updateURLParams,
        ],
    )

    const onChangeBuilding = useCallback(
        (e: any) => {
            const val = e.target.value
            setSelectedBuildingUID(val)
            const opt = buildingsAtSite.find((b) => b.uid === val)
            if (opt) {
                dispatch(setSelectedBuilding(opt))
            } else {
                dispatch(setSelectedBuilding({}))
            }
            updateURLParams(dynamicSelections, val)
        },
        [buildingsAtSite, dispatch, dynamicSelections, updateURLParams],
    )

    /** Open the entity instance panel for Add */
    const handleOpenFields = useCallback(
        (templateName: string, templateId: string) => {
            setOpenEntityInstance(true)
            setIsEdit(false)
            setEntityData({ templateName, templateId, entityId: '', orgId: '' })
        },
        [],
    )

    /** Open the entity instance panel for Edit */
    const handleClickEditIcon = useCallback(
        (entityId: string, templateName: string, templateId: string) => {
            setOpenEntityInstance(true)
            setIsEdit(true)
            setEntityData({
                templateName,
                templateId,
                entityId,
                orgId: org?.id || '',
            })
        },
        [org?.id],
    )

    /** Handle Add New for a dynamic hierarchy level */
    const handleLevelAddNew = useCallback(
        (levelIndex: number) => {
            const config = hierarchyConfig[levelIndex]
            const levelName =
                findEntityById(navList, config.entityTemplateId).name ||
                config.name

            if (levelIndex > 0) {
                // Set parent-child relation name
                const parentRelations =
                    hierarchyData[levelIndex - 1]?.relations || []
                const relationName = getRelationNameByTargetEntityTemplateId(
                    parentRelations,
                    config.entityTemplateId,
                )
                setParentChildRelationName(relationName)
            } else {
                setParentChildRelationName('')
            }

            setEditingLevel(levelIndex)
            handleOpenFields(levelName, config.entityTemplateId)
        },
        [hierarchyConfig, hierarchyData, navList, handleOpenFields],
    )

    /** Handle Edit Row for a dynamic hierarchy level */
    const handleLevelEditRow = useCallback(
        (levelIndex: number, item: { id: string; name: string }) => {
            const config = hierarchyConfig[levelIndex]
            const levelName =
                findEntityById(navList, config.entityTemplateId).name ||
                config.name
            const entityInstanceId = (dynamicEntities[levelIndex] || []).find(
                (e) => e.entityInstanceId === item.id,
            )?.entityInstanceId
            setEditingLevel(levelIndex)
            handleClickEditIcon(
                entityInstanceId || '',
                levelName,
                config.entityTemplateId,
            )
        },
        [hierarchyConfig, navList, dynamicEntities, handleClickEditIcon],
    )

    /** Handle Add New for building dropdown */
    const handleBuildingAddNew = useCallback(() => {
        const lastLevelIndex = hierarchyConfig.length - 1
        const parentRelations =
            lastLevelIndex >= 0
                ? hierarchyData[lastLevelIndex]?.relations || []
                : []
        const relationName = getRelationNameByTargetEntityTemplateId(
            parentRelations,
            buildingEntityTemplateId,
        )
        setParentChildRelationName(relationName)
        setEditingLevel('building')
        handleOpenFields(buildingName, buildingEntityTemplateId)
    }, [
        hierarchyConfig,
        hierarchyData,
        buildingEntityTemplateId,
        buildingName,
        handleOpenFields,
    ])

    /** Handle Edit Row for building dropdown */
    const handleBuildingEditRow = useCallback(
        (item: { id: string; name: string }) => {
            const entityInstanceId = buildingsAtSite.find(
                (b) => b.uid === item.id,
            )?.entityInstanceId
            setEditingLevel('building')
            handleClickEditIcon(
                entityInstanceId || '',
                buildingName,
                buildingEntityTemplateId,
            )
        },
        [
            buildingsAtSite,
            buildingName,
            buildingEntityTemplateId,
            handleClickEditIcon,
        ],
    )

    /** Reload entities after add/edit */
    const handleCloseFields = useCallback(
        async (reload: boolean) => {
            setOpenEntityInstance(false)
            if (reload) {
                let siteRecordsCache: any = null

                if (editingLevel === 'building') {
                    // Reload buildings — re-fetch from last dynamic level selection
                    const lastLevelIndex = hierarchyConfig.length - 1
                    const lastSelection = dynamicSelections[lastLevelIndex]
                    if (lastSelection) {
                        await fetchChildrenForLevel(
                            lastLevelIndex,
                            lastSelection,
                            false,
                            dynamicSelections,
                            selectedBuildingUID,
                        )
                    }
                    // Invalidate assets query
                    queryClient.invalidateQueries({
                        queryKey: [
                            'assetsAndSensorInBuilding',
                            selectedBuilding?.entityInstanceId,
                            buildingEntityTemplateId,
                            orgId,
                            user,
                        ],
                    })
                } else if (editingLevel === 0) {
                    try {
                        const queryParams = {
                            filterBy: [EntityStatus.ACTIVE],
                            filters: [],
                        }
                        const rootTemplateId =
                            hierarchyConfig[0].entityTemplateId
                        const response = await fetchEntityRecordsList(
                            orgId,
                            rootTemplateId,
                            queryParams,
                        )
                        if (rootTemplateId === siteEntityTemplateId) {
                            siteRecordsCache = response
                        }
                        const entities = response.data.map(
                            (item: any) =>
                                ({
                                    id: item.entityId,
                                    entityInstanceId: item.entityId,
                                    templateId: rootTemplateId,
                                    attributes: item.attributes,
                                    displayName: item.uid || '',
                                    uid: item.uid || '',
                                    status: item.status,
                                    permissionLevel: item.action,
                                }) as unknown as any,
                        )
                        setDynamicEntities((prev) => ({
                            ...prev,
                            [0]: entities,
                        }))

                        dispatch(
                            updateHierarchyLevelData({
                                levelIndex: 0,
                                data: { entities },
                            }),
                        )

                        // If a root entity is selected, cascade the refresh down to its children
                        if (dynamicSelections[0]) {
                            await fetchChildrenForLevel(
                                0,
                                dynamicSelections[0],
                                false,
                                dynamicSelections,
                                selectedBuildingUID,
                            )
                        }
                    } catch (_) {
                        // ignore fetch failure
                    }
                } else if (
                    typeof editingLevel === 'number' &&
                    editingLevel > 0
                ) {
                    // Reload this level by re-fetching from parent selection
                    const parentSelection = dynamicSelections[editingLevel - 1]
                    if (parentSelection) {
                        await fetchChildrenForLevel(
                            editingLevel - 1,
                            parentSelection,
                            false,
                            dynamicSelections,
                            selectedBuildingUID,
                        )
                    }
                }

                // If the entity being added/edited is at or above the Site level in the hierarchy,
                // a Site could have been created/modified (e.g. adding a Site inside a Territory form).
                // Globally refresh the MapView siteList in Redux to cover this corner case.
                const siteLevelIndex = hierarchyConfig.findIndex(
                    (c) => c.entityTemplateId === siteEntityTemplateId,
                )
                const shouldRefreshSites =
                    typeof editingLevel === 'number' &&
                    siteLevelIndex !== -1 &&
                    editingLevel <= siteLevelIndex

                if (shouldRefreshSites) {
                    try {
                        let siteRecords = siteRecordsCache
                        if (!siteRecords) {
                            const queryParams = {
                                filterBy: [EntityStatus.ACTIVE],
                                filters: [],
                            }
                            siteRecords = await fetchEntityRecordsList(
                                orgId,
                                siteEntityTemplateId,
                                queryParams,
                            )
                        }
                        const newSiteList = prepareSiteList(
                            siteRecords,
                            siteLocationAttributeId,
                            siteNameAttributeId,
                        )
                        dispatch(setSiteList(newSiteList))
                    } catch (_) {
                        // ignore fetch failure
                    }
                }

                // Globally refresh building and asset counts for the sites table if a site was created/edited
                // or if a building was created/edited.
                if (shouldRefreshSites || editingLevel === 'building') {
                    try {
                        const siteBuildingAssetCount =
                            await getBuildingsAssetsCount(orgId, true)
                        dispatch(
                            setSiteBuildingAssetCount(siteBuildingAssetCount),
                        )
                    } catch (_) {
                        // ignore fetch failure
                    }
                }
            }
            setEntityData({
                templateName: '',
                templateId: '',
                entityId: '',
                orgId: '',
            })
            setParentChildRelationName('')
        },
        [
            editingLevel,
            hierarchyConfig,
            dynamicSelections,
            fetchChildrenForLevel,
            queryClient,
            selectedBuilding,
            buildingEntityTemplateId,
            orgId,
            user,
            selectedBuildingUID,
            dispatch,
            siteEntityTemplateId,
            siteLocationAttributeId,
            siteNameAttributeId,
        ],
    )

    const assetsAndSensors = useGetAssetsAndSensorsInBuilding()

    const [floorLayoutContainerExpand, setFloorLayoutContainerExpand] =
        useState<boolean>(false)

    const toggleFloorLayoutConainerExpand = useCallback(() => {
        setFloorLayoutContainerExpand((prev) => !prev)
    }, [])

    const previouslyConfiguredSensorMarkers =
        assetsAndSensors.data?.previouslyConfiguredSensorMarkers || []
    const allAssetSensors = assetsAndSensors.data?.allAssetSensors || []
    const loadingAssetsAndSensors =
        loadingBuildings ||
        assetsAndSensors.isLoading ||
        assetsAndSensors.isFetching

    // Initialize root entities for levels > 0 and auto-select option
    useEffect(() => {
        if (initStatus.current !== 'pending') return

        if (hierarchyConfig && hierarchyConfig.length > 0 && hierarchyData[0]) {
            initStatus.current = 'started'
            const rootEntities = hierarchyData[0].entities || []
            setDynamicEntities((prev) => {
                if (prev[0] === rootEntities) return prev
                return {
                    ...prev,
                    [0]: rootEntities,
                }
            })
            if (rootEntities.length > 0 && !dynamicSelections[0]) {
                const urlSelection = getInitialSelectionForLevel(
                    hierarchyConfig[0],
                    0,
                )
                const matchedRoot = rootEntities.find(
                    (r) =>
                        r.uid === urlSelection ||
                        r.entityInstanceId === urlSelection,
                )
                const targetRoot = matchedRoot || rootEntities[0]
                if (targetRoot) {
                    const next = {
                        ...dynamicSelections,
                        [0]: targetRoot.entityInstanceId,
                    }
                    setDynamicSelections(next)
                    fetchChildrenForLevel(
                        0,
                        targetRoot.entityInstanceId,
                        true,
                        next,
                        searchParams.get(
                            URLSearchParamsKeys.selectedBuildingUID,
                        ) || undefined,
                        { 0: rootEntities },
                    )
                }
            }
        }
    }, [
        hierarchyData,
        hierarchyConfig,
        dynamicSelections,
        fetchChildrenForLevel,
        getInitialSelectionForLevel,
        searchParams,
    ])

    // When there are 0 pre-building levels, buildings are fetched directly
    // at app init time and stored in Redux as IBuildingOption[].
    useEffect(() => {
        if (initStatus.current !== 'pending') return

        if (hierarchyConfig.length === 0 && topLevelBuildings.length > 0) {
            initStatus.current = 'started'
            setBuildingsAtSite(topLevelBuildings)
            // Auto-select building from URL, fallback to first building
            const urlBuildingUID = searchParams.get(
                URLSearchParamsKeys.selectedBuildingUID,
            )
            const matchedBuilding = topLevelBuildings.find(
                (b) => b.uid === urlBuildingUID,
            )
            const targetBuilding = matchedBuilding || topLevelBuildings[0]
            if (targetBuilding) {
                setSelectedBuildingUID(targetBuilding.uid)
                dispatch(setSelectedBuilding(targetBuilding))
                initStatus.current = 'completed'
                updateURLParams(dynamicSelections, targetBuilding.uid)
            } else {
                initStatus.current = 'completed'
            }
        }
    }, [
        hierarchyConfig.length,
        topLevelBuildings,
        dispatch,
        searchParams,
        dynamicSelections,
        updateURLParams,
    ])

    return (
        <DndProvider backend={HTML5Backend}>
            <Box
                sx={{
                    backgroundColor: '#FFF',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                    {hierarchyConfig.map((config, index) => {
                        const entities = dynamicEntities[index] || []
                        const options = entities.map(
                            (e: RelatedEntityInstance) => ({
                                id: e.entityInstanceId,
                                name: e.uid,
                                canEdit: hasWritePermission,
                            }),
                        )
                        // Can add to level 0 always; subsequent levels need a parent selected
                        const canAdd =
                            hasWritePermission &&
                            (index === 0 || !!dynamicSelections[index - 1])

                        return (
                            <Grid key={index}>
                                <GenericDropdown
                                    label={config.name}
                                    name={`dynamicSelection${index}`}
                                    value={dynamicSelections[index] || ''}
                                    options={options}
                                    hideLabel={true}
                                    isLoading={dynamicLoading[index] || false}
                                    onChange={(e) =>
                                        handleDynamicChange(
                                            index,
                                            e.target.value,
                                        )
                                    }
                                    isMandatory={true}
                                    hasError={false}
                                    canAdd={canAdd}
                                    onClickAddNew={() =>
                                        handleLevelAddNew(index)
                                    }
                                    onClickEditRow={(item) =>
                                        handleLevelEditRow(index, item)
                                    }
                                />
                            </Grid>
                        )
                    })}

                    <Grid>
                        <GenericDropdown
                            label={t('chooseBuilding', { buildingName })}
                            name="buildingSelection"
                            value={selectedBuildingUID || ''}
                            options={buildingsAtSite.map((b) => ({
                                id: b.uid,
                                name: b.name,
                                canEdit: b.canEdit ?? hasWritePermission,
                            }))}
                            hideLabel={true}
                            isLoading={loadingBuildings}
                            onChange={(e) => onChangeBuilding(e)}
                            isMandatory={true}
                            hasError={false}
                            canAdd={
                                hasWritePermission &&
                                (hierarchyConfig.length === 0 ||
                                    !!dynamicSelections[
                                        hierarchyConfig.length - 1
                                    ])
                            }
                            onClickAddNew={handleBuildingAddNew}
                            onClickEditRow={handleBuildingEditRow}
                        />
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        mt: 1,
                        overflowY: 'hidden',
                        gap: 1,
                    }}
                >
                    {!floorLayoutContainerExpand && (
                        <>
                            <Box
                                sx={{
                                    overflowY: 'auto',
                                    minWidth: '20%',
                                    maxWidth: '20%',
                                    border: `1px solid #959595`,
                                    borderRadius: '8px',
                                }}
                            >
                                <ErrorBoundary>
                                    <AttributeTable />
                                </ErrorBoundary>
                            </Box>
                            <Box
                                sx={{
                                    overflowY: 'auto',
                                    minWidth: '20%',
                                    maxWidth: '20%',
                                    backgroundColor: '#FFF',
                                    border: `1px solid #959595`,
                                    borderRadius: '8px',
                                }}
                            >
                                <AssetSensorTable
                                    assetsType={t('assets')}
                                    allAssetSensors={allAssetSensors}
                                    isLoading={loadingAssetsAndSensors}
                                />
                            </Box>
                        </>
                    )}
                    <Box
                        sx={{
                            flexGrow: 1,
                            backgroundColor: '#FFF',
                            overflowY: 'auto',
                            border: `1px solid #959595`,
                            borderRadius: '8px',
                        }}
                    >
                        <SensorFloorMapping
                            floorLayoutContainerExpand={
                                floorLayoutContainerExpand
                            }
                            toggleFloorLayoutConainerExpand={
                                toggleFloorLayoutConainerExpand
                            }
                            previouslyConfiguredSensorMarkers={
                                previouslyConfiguredSensorMarkers
                            }
                            loadingAssetsAndSensors={loadingAssetsAndSensors}
                        />
                    </Box>
                </Box>

                {openEntityInstance && (
                    <EntityInstance
                        isEdit={isEdit}
                        hasAddRelation={{
                            parentInstanceId:
                                typeof editingLevel === 'number' &&
                                editingLevel > 0
                                    ? dynamicEntities[editingLevel - 1]?.find(
                                          (e) =>
                                              e.entityInstanceId ===
                                              dynamicSelections[
                                                  editingLevel - 1
                                              ],
                                      )?.entityInstanceId ?? ''
                                    : editingLevel === 'building'
                                      ? dynamicEntities[
                                            hierarchyConfig.length - 1
                                        ]?.find(
                                            (e) =>
                                                e.entityInstanceId ===
                                                dynamicSelections[
                                                    hierarchyConfig.length - 1
                                                ],
                                        )?.entityInstanceId ?? ''
                                      : '',
                            relationName: parentChildRelationName,
                        }}
                        handleCloseFields={handleCloseFields}
                        entityData={entityData}
                        instanceType={EntityInstanceTypes.NORMAL}
                    />
                )}
            </Box>
        </DndProvider>
    )
}
